import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCreatorStore } from '../stores/creatorStore'
import CollectionItemCard from '../components/CollectionItemCard'
import ItemSelector from '../components/ItemSelector'

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  collecting: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Collecting' },
  drafting: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Drafting' },
  completed: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Completed' },
}

export default function CollectionDetail() {
  const { collectionId } = useParams<{ collectionId: string }>()
  const navigate = useNavigate()
  const {
    currentCollection,
    templates,
    loading,
    error,
    fetchCollectionDetail,
    fetchTemplates,
    reorderItems,
    generateDraft,
    clearCurrent,
  } = useCreatorStore()

  const [selectorOpen, setSelectorOpen] = useState(false)
  const [draftModalOpen, setDraftModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null)
  const [draftMode, setDraftMode] = useState<'quick' | 'guided'>('quick')
  const [generating, setGenerating] = useState(false)

  const id = Number(collectionId)

  useEffect(() => {
    fetchCollectionDetail(id)
    fetchTemplates()
    return () => clearCurrent()
  }, [id, fetchCollectionDetail, fetchTemplates, clearCurrent])

  const items = currentCollection?.items || []
  const status = STATUS_STYLES[currentCollection?.status || 'collecting'] || STATUS_STYLES.collecting

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const reordered = items.map((item, i) => ({
      id: item.id,
      position: i === index ? index - 1 : i === index - 1 ? index : i,
    }))
    reorderItems(id, reordered)
  }

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return
    const reordered = items.map((item, i) => ({
      id: item.id,
      position: i === index ? index + 1 : i === index + 1 ? index : i,
    }))
    reorderItems(id, reordered)
  }

  const handleGenerate = async () => {
    if (!selectedTemplate || generating) return
    setGenerating(true)
    try {
      const result = await generateDraft(id, draftMode, selectedTemplate)
      setDraftModalOpen(false)
      if (draftMode === 'guided' && result.thread_id) {
        navigate(`/research/${result.thread_id}`)
      } else {
        // Quick draft: poll and navigate to draft view
        navigate(`/creator/${id}/draft`)
      }
    } finally {
      setGenerating(false)
    }
  }

  if (loading && !currentCollection) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && !currentCollection) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <p className="text-error text-sm mb-4">{error}</p>
        <button
          onClick={() => fetchCollectionDetail(id)}
          className="px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!currentCollection) {
    return (
      <div className="p-4 text-center text-text-muted">Collection not found</div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-elevated shrink-0">
        <button onClick={() => navigate('/creator')} aria-label="Back to Creator Studio" className="text-text-secondary hover:text-text-primary">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h1 className="font-medium truncate flex-1">{currentCollection.title}</h1>
        <span className={`shrink-0 px-2 py-0.5 rounded text-xs ${status.bg} ${status.text}`}>
          {status.label}
        </span>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <p className="text-text-muted mb-4">No items yet. Add research from your threads.</p>
            <button
              onClick={() => setSelectorOpen(true)}
              className="bg-accent text-bg px-5 py-2.5 rounded-lg font-medium hover:bg-accent-hover transition-colors"
            >
              Add Items
            </button>
          </div>
        ) : (
          items.map((item, index) => (
            <CollectionItemCard
              key={item.id}
              item={item}
              collectionId={id}
              index={index}
              total={items.length}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
            />
          ))
        )}
      </div>

      {/* Bottom action bar */}
      {items.length > 0 && (
        <div className="flex gap-3 px-4 py-3 bg-surface border-t border-elevated shrink-0">
          <button
            onClick={() => setSelectorOpen(true)}
            className="flex-1 py-2.5 rounded-lg bg-elevated text-text-secondary font-medium hover:bg-bg transition-colors"
          >
            Add More
          </button>
          <button
            onClick={() => setDraftModalOpen(true)}
            className="flex-1 py-2.5 rounded-lg bg-accent text-bg font-medium hover:bg-accent-hover transition-colors"
          >
            Generate Draft
          </button>
        </div>
      )}

      {/* Item Selector */}
      <ItemSelector
        open={selectorOpen}
        collectionId={id}
        onClose={() => {
          setSelectorOpen(false)
          fetchCollectionDetail(id)
        }}
      />

      {/* Draft Generation Modal */}
      {draftModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
          onKeyDown={(e) => e.key === 'Escape' && setDraftModalOpen(false)}
          onClick={(e) => { if (e.target === e.currentTarget) setDraftModalOpen(false) }}
        >
          <div className="bg-surface w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
            <h2 className="text-lg font-semibold">Generate Draft</h2>

            {/* Template selector */}
            <div>
              <label className="block text-sm text-text-secondary mb-1">Template</label>
              <select
                value={selectedTemplate || ''}
                onChange={(e) => setSelectedTemplate(Number(e.target.value))}
                autoFocus
                className="w-full bg-elevated text-text-primary rounded-lg px-4 py-3 outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Select a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Mode toggle */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Mode</label>
              <div className="flex rounded-lg bg-elevated p-1">
                <button
                  onClick={() => setDraftMode('quick')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                    draftMode === 'quick'
                      ? 'bg-accent text-bg'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Quick Draft
                </button>
                <button
                  onClick={() => setDraftMode('guided')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                    draftMode === 'guided'
                      ? 'bg-accent text-bg'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Guided Conversation
                </button>
              </div>
              <p className="text-xs text-text-muted mt-1.5">
                {draftMode === 'quick'
                  ? 'AI generates a complete draft from your collection.'
                  : 'AI guides you through refining the content interactively.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setDraftModalOpen(false)}
                className="flex-1 py-3 rounded-lg bg-elevated text-text-secondary font-medium hover:bg-bg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!selectedTemplate || generating}
                className="flex-1 py-3 rounded-lg bg-accent text-bg font-medium hover:bg-accent-hover transition-colors disabled:opacity-30"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
